import { useEffect, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Alert
} from 'antd';
import { CheckCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { withDevModalFilePath } from '../common';
import { trpc } from '../../lib/api';
import type { WorkScheduleResult } from '../../../../shared/types/work-schedule.types';
import type { WorkScheduleDetail } from './DetailModal';

const { Modal } = withDevModalFilePath('client/src/components/WorkSchedule/CompleteWorkModal.tsx');
const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const RESULT_OPTIONS: Array<{ label: string; value: WorkScheduleResult }> = [
  { label: '良好', value: 'good' },
  { label: '要注意', value: 'attention_needed' },
  { label: '修理必要', value: 'repair_required' }
];

interface CompleteWorkModalProps {
  open: boolean;
  schedule: WorkScheduleDetail | null;
  onClose: () => void;
  onCompleted?: () => void;
}

/**
 * 作業完了報告モーダル
 * - 作業内容、発見事項、推奨事項を入力
 * - 作業結果を選択
 * - 承認が必要な場合は pending_approval に、不要な場合は completed に遷移
 */
export function CompleteWorkModal({
  open,
  schedule,
  onClose,
  onCompleted
}: CompleteWorkModalProps): JSX.Element {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const { message } = App.useApp();

  const completeSchedule = trpc.workSchedule.complete.useMutation({
    onSuccess: (data) => {
      const requiresApproval = data.status === 'pending_approval';
      if (requiresApproval) {
        message.success('作業完了報告を送信しました。承認をお待ちください。');
      } else {
        message.success('作業が完了しました。');
      }
      form.resetFields();
      onCompleted?.();
      onClose();
    },
    onError: (error) => {
      message.error(error.message || '作業完了報告に失敗しました');
    },
    onSettled: () => {
      setSubmitting(false);
    }
  });

  useEffect(() => {
    if (open && schedule) {
      form.setFieldsValue({
        workPerformed: schedule.workPerformed ?? '',
        findings: schedule.findings ?? '',
        recommendations: schedule.recommendations ?? '',
        result: schedule.result ?? undefined
      });
    }
  }, [open, schedule, form]);

  const handleSubmit = async () => {
    if (!schedule) {
      return;
    }

    try {
      const values = await form.validateFields();
      setSubmitting(true);

      await completeSchedule.mutateAsync({
        id: schedule.id,
        workPerformed: values.workPerformed,
        findings: values.findings,
        recommendations: values.recommendations,
        result: values.result,
        actualEnd: new Date()
      });
    } catch {
      // バリデーションエラーは何もしない
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined />
          <span>作業完了報告</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={submitting}>
          キャンセル
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<CheckCircleOutlined />}
          loading={submitting}
          onClick={handleSubmit}
        >
          完了報告を送信
        </Button>
      ]}
      width={600}
      destroyOnHidden
    >
      {schedule && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message="作業完了報告"
            description={
              <div>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text strong>{schedule.title || '名称未設定のスケジュール'}</Text>
                </Paragraph>
                <Text type="secondary">
                  作業内容と結果を入力して、完了報告を送信してください。
                  {schedule.workOrderTemplateId && (
                    <>
                      <br />
                      ※ この作業は承認が必要な場合があります。
                    </>
                  )}
                </Text>
              </div>
            }
            type="info"
            icon={<FileTextOutlined />}
            showIcon
          />

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              workPerformed: '',
              findings: '',
              recommendations: '',
              result: undefined
            }}
          >
            <Form.Item
              label="作業内容"
              name="workPerformed"
              rules={[
                { required: true, message: '作業内容を入力してください' }
              ]}
            >
              <TextArea
                rows={4}
                placeholder="実施した作業の内容を入力してください"
              />
            </Form.Item>

            <Form.Item
              label="発見事項"
              name="findings"
            >
              <TextArea
                rows={3}
                placeholder="作業中に発見した問題や気になった点を入力してください（任意）"
              />
            </Form.Item>

            <Form.Item
              label="推奨事項"
              name="recommendations"
            >
              <TextArea
                rows={3}
                placeholder="今後の対応や改善提案を入力してください（任意）"
              />
            </Form.Item>

            <Form.Item
              label="作業結果"
              name="result"
              rules={[
                { required: true, message: '作業結果を選択してください' }
              ]}
            >
              <Select
                placeholder="作業結果を選択"
                options={RESULT_OPTIONS}
              />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  );
}
